import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { escapeHtml } from "./escape_html"

// Configure marked to match server-side Redcarpet output
marked.setOptions({
  breaks: true,           // Convert \n to <br> (matches Redcarpet hard_wrap)
  gfm: true,              // GitHub Flavored Markdown
  headerIds: false,       // Don't add IDs to headers
  mangle: false,          // Don't mangle email links
  pedantic: false,
  async: false
})

// Custom renderer to add target="_blank" to links (matches Redcarpet config)
const renderer = new marked.Renderer()
const originalLinkRenderer = renderer.link.bind(renderer)
renderer.link = function(href, title, text) {
  // Handle the new marked signature which passes token object
  if (typeof href === 'object') {
    const token = href
    const link = `<a href="${token.href}" target="_blank" rel="noopener noreferrer"${token.title ? ` title="${token.title}"` : ''}>${token.text}</a>`
    return link
  }
  // Fallback for older signature
  const link = originalLinkRenderer(href, title, text)
  return link.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ')
}

marked.use({ renderer })

/**
 * Renders markdown content that may be incomplete (during streaming).
 * Handles unclosed code blocks, inline code, bold, italic, etc.
 *
 * @param {string} content - The markdown content (may be incomplete)
 * @returns {string} - Safe HTML that can be rendered
 */
export function renderStreamingMarkdown(content) {
  if (!content) return ''

  let safeContent = content

  // Handle incomplete fenced code blocks (```)
  const tripleBackticks = (safeContent.match(/```/g) || []).length
  if (tripleBackticks % 2 !== 0) {
    // Incomplete code block - close it temporarily
    safeContent += '\n```'
  }

  // Handle incomplete inline code (`)
  // Count single backticks that are NOT part of triple backticks
  const singleBacktickMatches = safeContent.match(/(?<!`)`(?!`)/g) || []
  if (singleBacktickMatches.length % 2 !== 0) {
    safeContent += '`'
  }

  // Handle incomplete bold (**)
  const doubleStar = (safeContent.match(/\*\*/g) || []).length
  if (doubleStar % 2 !== 0) {
    safeContent += '**'
  }

  // Handle incomplete italic (*) - but not inside **
  // This is tricky, so we use a simpler heuristic:
  // Count single asterisks not adjacent to another asterisk
  const singleStarMatches = safeContent.match(/(?<!\*)\*(?!\*)/g) || []
  if (singleStarMatches.length % 2 !== 0) {
    safeContent += '*'
  }

  // Handle incomplete underline bold (__)
  const doubleUnderscore = (safeContent.match(/__/g) || []).length
  if (doubleUnderscore % 2 !== 0) {
    safeContent += '__'
  }

  // Handle incomplete underline italic (_) - similar to single asterisk
  const singleUnderscoreMatches = safeContent.match(/(?<!_)_(?!_)/g) || []
  if (singleUnderscoreMatches.length % 2 !== 0) {
    safeContent += '_'
  }

  // Handle incomplete strikethrough (~~)
  const doubleTilde = (safeContent.match(/~~/g) || []).length
  if (doubleTilde % 2 !== 0) {
    safeContent += '~~'
  }

  // Handle incomplete links - [text](url
  // Check if we have an unclosed link
  const openBrackets = (safeContent.match(/\[/g) || []).length
  const closeBrackets = (safeContent.match(/\]/g) || []).length
  if (openBrackets > closeBrackets) {
    // We have an unclosed bracket, close it
    safeContent += ']()'
  } else {
    // Check for incomplete URL part: ](url without closing )
    const linkPattern = /\]\([^)]*$/
    if (linkPattern.test(safeContent)) {
      safeContent += ')'
    }
  }

  try {
    return DOMPurify.sanitize(marked.parse(safeContent))
  } catch (e) {
    // Fallback to basic HTML if marked fails
    console.error('[streaming_markdown] Parse error:', e)
    return basicHtmlFormat(content)
  }
}

/**
 * Renders complete markdown content (after streaming is done).
 * This is simpler since we don't need to handle incomplete syntax.
 *
 * @param {string} content - Complete markdown content
 * @returns {string} - HTML string
 */
export function renderMarkdown(content) {
  if (!content) return ''

  try {
    return DOMPurify.sanitize(marked.parse(content))
  } catch (e) {
    console.error('[streaming_markdown] Parse error:', e)
    return basicHtmlFormat(content)
  }
}

/**
 * Basic HTML formatting fallback (original formatContent logic)
 * Used when markdown parsing fails
 *
 * @param {string} content - Raw text content
 * @returns {string} - Basic HTML
 */
function basicHtmlFormat(content) {
  const text = (content || '').trim()
  if (!text) return ''

  return text
    .split(/\n{2,}/)
    .map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('')
}
